/**
 * Gemini Live native-audio voice engine (parallel to legacy useVoiceConversationEngine).
 * Bridges scripted flows via Live tool {@link GEMINI_LIVE_SCRIPT_TOOL_NAME}.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  GoogleGenAI,
  Modality,
  Type,
  FunctionCallingConfigMode,
} from '@google/genai';
import {
  fetchGeminiLiveEphemeralToken,
  downsampleBuffer,
  floatTo16BitPCM,
  encodeLittleEndianPcm16Base64,
  decodeLittleEndianPcm16Base64,
  parsePcmRateFromMime,
} from 'services/voice/geminiLiveSession';
import { GEMINI_LIVE_SCRIPT_TOOL_NAME } from 'services/voice/geminiLiveConstants';
import { GEMINI_LIVE_DEFAULT_VOICE } from 'services/voice/geminiLiveVoices';
import { isLiveSessionSendReady } from 'services/voice/geminiLiveWebSocketUtils';
import {
  mergeAssistantTranscriptionChunk,
  getMedicolegalDisclaimer,
  isMedicolegalDisclaimerText,
  MEDICOLEGAL_DISCLAIMER_ESTIMATED_SPEECH_S,
  shouldEndConversationFromUserSpeech,
} from '../../features/patient/components/symptomCheck/voice-conversation/conversationContract';
import { scheduleVerbalEndConversationAck } from './scheduleVerbalEndConversationAck';
import { createVoiceLogAdapter } from './voiceLogAdapter';

const INPUT_PCM_RATE = 16000;
const OUTPUT_PCM_RATE = 24000;
/** Seconds after the last scheduled assistant sample before we send mic audio again (reduces echo → false `interrupted`). */
const ASSISTANT_PLAYBACK_TAIL_S = 0.35;
const MIN_COMPLETE_TEARDOWN_MS = 1200;
const MAX_COMPLETE_TEARDOWN_MS = 45000;
const COMPLETION_DRAIN_CHECK_MS = 250;
const ASSISTANT_TRANSCRIPT_FINALIZE_DEBOUNCE_MS = 450;
const INTERRUPTED_SUFFIX = ' (cut off)';
const DEFERRED_BRAIN_TURN_AFTER_DISCLAIMER_MS = 400;

function normalizeAssistantText(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isLikelySameAssistantUtterance(a, b) {
  const normalizedA = normalizeAssistantText(a);
  const normalizedB = normalizeAssistantText(b);
  if (!normalizedA || !normalizedB) return false;
  if (normalizedA === normalizedB) return true;
  return normalizedA.includes(normalizedB) || normalizedB.includes(normalizedA);
}

/**
 * Gemini 3.1 Live: `sendClientContent` is only for seeding history with `historyConfig`;
 * an empty `sendClientContent({ turnComplete: true })` can make the server close immediately.
 * Use `sendRealtimeInput({ text })` to start the first turn instead.
 * https://ai.google.dev/gemini-api/docs/live-guide#incremental-content-updates
 */
function prefersRealtimeTextOpeningNudge(liveModelId) {
  if (!liveModelId || typeof liveModelId !== 'string') return false;
  return /gemini-3\.1/i.test(liveModelId) && /live/i.test(liveModelId);
}

/** Inline worklet: copies mono input to main thread (ScriptProcessorNode is deprecated). */
const GEMINI_LIVE_CAPTURE_WORKLET = `
class GeminiLiveCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const ch0 = inputs[0] && inputs[0][0];
    if (!ch0 || !ch0.length) return true;
    const copy = new Float32Array(ch0.length);
    copy.set(ch0);
    this.port.postMessage(copy, [copy.buffer]);
    return true;
  }
}
registerProcessor('gemini-live-capture', GeminiLiveCaptureProcessor);
`;

function buildScriptToolDeclaration() {
  return {
    name: GEMINI_LIVE_SCRIPT_TOOL_NAME,
    description:
      'Call after the user finishes answering (end of their turn). Pass their latest transcript. ' +
      'The host returns the next thing you should say and whether the interview is complete.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        userTranscript: {
          type: Type.STRING,
          description: "The user's words for this turn (may be empty only for the very first scripted question).",
        },
      },
      required: ['userTranscript'],
    },
  };
}

/**
 * Same Firestore-backed strings as classic {@link VoiceConversation} (symptom-checker-voice-prompts).
 * Injected so Live behavior tracks Query Manager edits, not only the opening line.
 */
function voicePromptsSystemAppendix(voicePrompts) {
  if (!voicePrompts || typeof voicePrompts !== 'object') return '';
  const parts = [];
  const push = (label, val) => {
    if (typeof val === 'string' && val.trim()) parts.push(`${label}: ${val.trim()}`);
  };
  push('Chief-complaint clarification (if needed)', voicePrompts.chiefComplaintClarification);
  push('Proxy confirmation question', voicePrompts.proxyConfirmationQuestion);
  push('Minor detected question', voicePrompts.minorDetectedQuestion);
  push('Minor / under-18 message', voicePrompts.minorUserMessage);
  push('Refinement transition', voicePrompts.refinementTransitionQuestion);
  push('Closing (normal)', voicePrompts.closing);
  push('Closing (max questions)', voicePrompts.closingMaxReached);
  push('Still listening reassurance', voicePrompts.stillListening);
  if (!parts.length) return '';
  return (
    '\n\nReference lines from the same voice prompt document as the classic app (speak naturally; ' +
    'the scripted interview still flows through get_next_utterance):\n' +
    parts.map((p) => `- ${p}`).join('\n')
  );
}

function buildSystemInstruction({
  openingMessage,
  language,
  askFirstQuestionAfterOpening,
  askDisclaimerAfterOpening,
  disclaimerText,
  voicePrompts,
}) {
  const lang = language || 'en-US';
  let s =
    'You are a warm, concise clinical intake voice assistant. Keep replies short and natural. ' +
    `Prefer conversational language appropriate for locale ${lang}. ` +
    'Do not invent your own clinical intake questions: after the opening, every substantive question and closing line ' +
    'must come from calling get_next_utterance and speaking its nextUtterance verbatim (you may only add very minor punctuation). ' +
    'After each user answer, you must call get_next_utterance exactly once with their transcript. ' +
    'When you receive the tool result, speak nextUtterance exactly once and do not insert extra filler lines before or after it. ' +
    'Do not add your own medical disclaimers or emergency warnings unless the scripted line from get_next_utterance says so. ' +
    'If policy forces a short safety statement, keep it to one brief sentence, then immediately continue with the exact nextUtterance in the same turn. ' +
    'If nextUtterance is empty, produce no speech and wait silently. ' +
    'If endSessionNow is true in a tool result, produce no speech—the session is ending. ' +
    'If isComplete is true and nextUtterance is non-empty, speak nextUtterance once. ' +
    'If isComplete is true and nextUtterance is empty and endSessionNow is not set, do not add a closing line of your own. ';
  s +=
    'CRITICAL: Never speak for the user. Never state or predict the ' +
    "user's age, sex, symptoms, or any answer on their behalf. " +
    'After asking a question, emit NO further audio of any kind until ' +
    'the next get_next_utterance tool call is triggered by real user ' +
    'speech. Silence is correct. Do not fill silence. ';
  if (askDisclaimerAfterOpening && disclaimerText) {
    s +=
      'Your first spoken turn is exactly this greeting, spoken word ' +
      'for word as ONE continuous utterance with no pause or turn-end ' +
      'between the sentences: ' +
      `"${disclaimerText} ${openingMessage}" ` +
      'Speak the entire greeting exactly as written — disclaimer first, ' +
      'then opening question, without stopping between them. ' +
      'Do not paraphrase any part of the greeting. ' +
      'Only after you have spoken BOTH sentences completely may you ' +
      'stop and wait silently for the user to respond. ' +
      'Do not call get_next_utterance until the user has responded. ';
  } else {
    s += `Your first spoken turn must convey this opening (stay faithful to wording when possible): "${openingMessage}" `;
    if (askFirstQuestionAfterOpening) {
      s +=
        'Immediately after that opening, call get_next_utterance with userTranscript "" to fetch the first scripted question. ';
    } else {
      s +=
        'Your first turn is only that opening in audio; do not call get_next_utterance until the user has finished a spoken response. ';
    }
  }
  s += voicePromptsSystemAppendix(voicePrompts);
  return s;
}

/**
 * @param {Object} options
 * @param {function} options.onPauseDetected - Same contract as legacy engine.
 * @param {string} [options.openingMessage]
 * @param {string} [options.language]
 * @param {Object} [options.context]
 * @param {function} [options.onComplete]
 * @param {function} [options.onUserSpeech]
 * @param {function} [options.onLogEntry]
 * @param {function} [options.onLiveTranscript]
 * @param {boolean} [options.askFirstQuestionAfterOpening]
 * @param {boolean} [options.askDisclaimerAfterOpening]
 * @param {function} [options.onDisclaimerDelivered] - Fired when disclaimer is folded into opening (Gemini Live).
 * @param {string} [options.liveSpeechVoice] - Gemini Live prebuilt voice name (see geminiLiveVoices.js).
 * @param {Array} [options.controlledConversationLog]
 * @param {function} [options.onConversationLogChange]
 * @param {Array} [options.controlledQuestionsAsked]
 * @param {function} [options.onQuestionsAskedChange]
 */
function useGeminiLiveVoiceConversationEngine({
  onPauseDetected,
  openingMessage = 'Hello! How can I help you today?',
  language = 'en-US',
  context,
  onComplete,
  onUserSpeech,
  onLogEntry,
  onLiveTranscript,
  askFirstQuestionAfterOpening = false,
  askDisclaimerAfterOpening = true,
  onDisclaimerDelivered,
  liveSpeechVoice,
  controlledConversationLog,
  onConversationLogChange,
  controlledQuestionsAsked,
  onQuestionsAskedChange,
} = {}) {
  const [isActive, setIsActive] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);
  const [audioConfirmed, setAudioConfirmed] = useState(false);
  const [endConversationAcknowledged, setEndConversationAcknowledged] = useState(false);

  const [questionsAskedInternal, setQuestionsAskedInternal] = useState([]);
  const [conversationLogInternal, setConversationLogInternal] = useState([]);

  const questionsAsked =
    controlledQuestionsAsked !== undefined ? controlledQuestionsAsked : questionsAskedInternal;
  const conversationLog =
    controlledConversationLog !== undefined ? controlledConversationLog : conversationLogInternal;

  const setQuestionsAsked = useCallback(
    (valueOrUpdater) => {
      if (onQuestionsAskedChange) {
        if (typeof valueOrUpdater === 'function') {
          onQuestionsAskedChange((prev) => valueOrUpdater(prev));
        } else {
          onQuestionsAskedChange(valueOrUpdater);
        }
      } else {
        setQuestionsAskedInternal(valueOrUpdater);
      }
    },
    [onQuestionsAskedChange]
  );

  const setConversationLog = useCallback(
    (valueOrUpdater) => {
      if (onConversationLogChange) {
        if (typeof valueOrUpdater === 'function') {
          onConversationLogChange((prev) => valueOrUpdater(prev));
        } else {
          onConversationLogChange(valueOrUpdater);
        }
      } else {
        setConversationLogInternal(valueOrUpdater);
      }
    },
    [onConversationLogChange]
  );

  const sessionRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const inputAudioContextRef = useRef(null);
  const outputAudioContextRef = useRef(null);
  const captureNodeRef = useRef(null);
  const nextPlayTimeRef = useRef(0);
  /** outputAudioContext.currentTime must be >= this before mic audio is sent (half-duplex + tail). */
  const micOpenAtRef = useRef(0);
  const activeOutputSourcesRef = useRef([]);
  const conversationLogRef = useRef([]);
  const questionsAskedRef = useRef([]);
  const onPauseDetectedRef = useRef(onPauseDetected);
  const contextRef = useRef(context);
  const onCompleteRef = useRef(onComplete);
  const onUserSpeechRef = useRef(onUserSpeech);
  const onLogEntryRef = useRef(onLogEntry);
  const onLiveTranscriptRef = useRef(onLiveTranscript);
  const isActiveRef = useRef(false);
  const openingMessageRef = useRef(openingMessage);
  const languageRef = useRef(language);
  const askFirstQuestionAfterOpeningRef = useRef(askFirstQuestionAfterOpening);
  const askDisclaimerAfterOpeningRef = useRef(askDisclaimerAfterOpening);
  const postOpeningDisclaimerTimerRef = useRef(null);
  const disclaimerDeliveredRef = useRef(false);
  /** When true, outputTranscription chunks must not overwrite pinned scripted text. */
  const suppressTranscriptUpdateRef = useRef(false);
  const onDisclaimerDeliveredRef = useRef(onDisclaimerDelivered);
  const liveSpeechVoiceRef = useRef(GEMINI_LIVE_DEFAULT_VOICE);
  const conversationScrollRef = useRef(null);
  const adapterRef = useRef(null);
  /**
   * True when real inputTranscription has arrived while the mic gate was open
   * since the last assistant turn. Used to reject fabricated userTranscripts in
   * model-issued get_next_utterance calls (model predicting the patient's answer).
   */
  const realUserAudioDetectedRef = useRef(false);
  const assistantTranscriptBufferRef = useRef('');
  const assistantTranscriptFinalizeTimerRef = useRef(null);
  const assistantTurnExpectedTextRef = useRef('');
  const completionDrainTimerRef = useRef(null);
  const completionStopDeadlineRef = useRef(0);
  /** Tool returned isComplete; server may close the socket before delayed stopConversation — not an error. */
  const conversationCompletingRef = useRef(false);
  /** User clicked End (or we called stop); avoid treating the following socket close as a failure. */
  const intentionalDisconnectRef = useRef(false);
  /** True after WebSocket onopen; required before beginLiveAudio(). */
  const connectionReadyRef = useRef(false);
  const connectionReadyHandlersRef = useRef(null);
  const liveModelIdRef = useRef(null);

  const clearConnectionReadyPromise = useCallback(({ rejectReason } = {}) => {
    connectionReadyRef.current = false;
    const handlers = connectionReadyHandlersRef.current;
    connectionReadyHandlersRef.current = null;
    if (handlers && rejectReason) {
      const err =
        rejectReason instanceof Error ? rejectReason : new Error(String(rejectReason));
      handlers.reject(err);
    }
  }, []);

  const resolveConnectionReady = useCallback(() => {
    connectionReadyRef.current = true;
    const handlers = connectionReadyHandlersRef.current;
    connectionReadyHandlersRef.current = null;
    handlers?.resolve();
  }, []);

  useEffect(() => {
    onPauseDetectedRef.current = onPauseDetected;
  }, [onPauseDetected]);
  useEffect(() => {
    contextRef.current = context;
  }, [context]);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);
  useEffect(() => {
    onUserSpeechRef.current = onUserSpeech;
  }, [onUserSpeech]);
  useEffect(() => {
    onLogEntryRef.current = onLogEntry;
  }, [onLogEntry]);
  useEffect(() => {
    onLiveTranscriptRef.current = onLiveTranscript;
  }, [onLiveTranscript]);
  useEffect(() => {
    openingMessageRef.current = openingMessage;
  }, [openingMessage]);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);
  useEffect(() => {
    askFirstQuestionAfterOpeningRef.current = askFirstQuestionAfterOpening;
  }, [askFirstQuestionAfterOpening]);
  useEffect(() => {
    askDisclaimerAfterOpeningRef.current = askDisclaimerAfterOpening;
  }, [askDisclaimerAfterOpening]);
  useEffect(() => {
    onDisclaimerDeliveredRef.current = onDisclaimerDelivered;
  }, [onDisclaimerDelivered]);
  useEffect(() => {
    const v = typeof liveSpeechVoice === 'string' && liveSpeechVoice.trim() ? liveSpeechVoice.trim() : '';
    liveSpeechVoiceRef.current = v || GEMINI_LIVE_DEFAULT_VOICE;
  }, [liveSpeechVoice]);

  useEffect(() => {
    conversationLogRef.current = conversationLog;
  }, [conversationLog]);
  useEffect(() => {
    questionsAskedRef.current = questionsAsked;
  }, [questionsAsked]);

  const appendLog = useCallback(
    (entry) => {
      setConversationLog((prev) => {
        const next = [...prev, entry];
        conversationLogRef.current = next;
        onLogEntryRef.current?.(entry, next);
        return next;
      });
    },
    [setConversationLog]
  );

  if (!adapterRef.current) {
    adapterRef.current = createVoiceLogAdapter({
      onCommit: (entry) => appendLog(entry),
      onLivePartial: (text) => onLiveTranscriptRef.current?.(text),
      onBeforeCommit: () => {
        /* onCommit → appendLog → onLogEntry */
      },
    });
  }

  const updateLastAssistantLog = useCallback(
    (nextText) => {
      const trimmed = String(nextText || '').trim();
      if (!trimmed) return;
      setConversationLog((prev) => {
        const idx = [...prev].map((entry) => entry?.speaker).lastIndexOf('assistant');
        if (idx < 0) return prev;
        const current = prev[idx];
        if (!current || current.text === trimmed) return prev;
        const updatedEntry = { ...current, text: trimmed, timestamp: Date.now() };
        const next = [...prev];
        next[idx] = updatedEntry;
        conversationLogRef.current = next;
        onLogEntryRef.current?.(updatedEntry, next);
        return next;
      });
    },
    [setConversationLog]
  );

  const beginAssistantTurn = useCallback((expectedText = '') => {
    const expected = String(expectedText || '').trim();
    // New assistant turn → require fresh real user audio before trusting any
    // userTranscript the model sends next (blocks model-fabricated patient answers).
    realUserAudioDetectedRef.current = false;
    // Seed with scripted line so short partial transcription chunks do not
    // overwrite a full assistant sentence that was already shown to the user.
    assistantTranscriptBufferRef.current = expected;
    assistantTurnExpectedTextRef.current = expected;
    if (assistantTranscriptFinalizeTimerRef.current) {
      clearTimeout(assistantTranscriptFinalizeTimerRef.current);
      assistantTranscriptFinalizeTimerRef.current = null;
    }
  }, []);

  const absorbAssistantTranscriptionChunk = useCallback(
    (chunkText) => {
      if (suppressTranscriptUpdateRef.current) {
        return assistantTranscriptBufferRef.current;
      }
      const incoming = String(chunkText || '').trim();
      if (!incoming) return assistantTranscriptBufferRef.current;
      const prior = assistantTranscriptBufferRef.current;
      const merged = mergeAssistantTranscriptionChunk(
        prior,
        incoming,
        isLikelySameAssistantUtterance
      );
      assistantTranscriptBufferRef.current = merged;

      if (assistantTranscriptFinalizeTimerRef.current) {
        clearTimeout(assistantTranscriptFinalizeTimerRef.current);
      }
      assistantTranscriptFinalizeTimerRef.current = setTimeout(() => {
        assistantTranscriptFinalizeTimerRef.current = null;
        if (assistantTranscriptBufferRef.current) {
          updateLastAssistantLog(assistantTranscriptBufferRef.current);
        }
      }, ASSISTANT_TRANSCRIPT_FINALIZE_DEBOUNCE_MS);

      return merged;
    },
    [updateLastAssistantLog]
  );

  const markLastAssistantInterrupted = useCallback(() => {
    const spokenSoFar = String(assistantTranscriptBufferRef.current || '').trim();
    setConversationLog((prev) => {
      const idx = [...prev].map((entry) => entry?.speaker).lastIndexOf('assistant');
      if (idx < 0) return prev;
      const current = prev[idx];
      if (!current) return prev;
      const base = spokenSoFar || String(current.text || '').trim();
      if (!base || base.endsWith(INTERRUPTED_SUFFIX)) return prev;
      const updatedEntry = {
        ...current,
        text: `${base}${INTERRUPTED_SUFFIX}`,
        timestamp: Date.now(),
      };
      const next = [...prev];
      next[idx] = updatedEntry;
      conversationLogRef.current = next;
      onLogEntryRef.current?.(updatedEntry, next);
      return next;
    });
  }, [setConversationLog]);

  const clearCompletionDrainTimer = useCallback(() => {
    if (completionDrainTimerRef.current) {
      clearTimeout(completionDrainTimerRef.current);
      completionDrainTimerRef.current = null;
    }
  }, []);

  const clearPostOpeningDisclaimerTimer = useCallback(() => {
    if (postOpeningDisclaimerTimerRef.current) {
      clearTimeout(postOpeningDisclaimerTimerRef.current);
      postOpeningDisclaimerTimerRef.current = null;
    }
  }, []);

  const haltLiveAssistantPlayback = useCallback(() => {
    const octx = outputAudioContextRef.current;
    const nowT = octx?.currentTime || 0;
    for (const src of activeOutputSourcesRef.current) {
      try {
        src.stop();
      } catch (_) {
        /* ignore */
      }
    }
    activeOutputSourcesRef.current = [];
    nextPlayTimeRef.current = nowT;
    micOpenAtRef.current = nowT;
    setIsSpeaking(false);
  }, []);

  const scheduleCompletionStopAfterPlaybackDrain = useCallback(() => {
    clearCompletionDrainTimer();
    completionDrainTimerRef.current = setTimeout(() => {
      completionDrainTimerRef.current = null;
      if (!conversationCompletingRef.current) return;
      const outputCtx = outputAudioContextRef.current;
      const now = outputCtx?.currentTime || 0;
      const scheduledEnd = nextPlayTimeRef.current || 0;
      const hasActiveSources = activeOutputSourcesRef.current.length > 0;
      const playbackDrained =
        !hasActiveSources &&
        (!outputCtx || now >= scheduledEnd + ASSISTANT_PLAYBACK_TAIL_S);
      const timedOut =
        completionStopDeadlineRef.current > 0 &&
        Date.now() >= completionStopDeadlineRef.current;

      if (playbackDrained || timedOut) {
        stopConversationRef.current?.();
        return;
      }

      scheduleCompletionStopAfterPlaybackDrain();
    }, COMPLETION_DRAIN_CHECK_MS);
  }, [clearCompletionDrainTimer]);

  const handleToolCalls = useCallback(
    async (session, functionCalls) => {
      if (!functionCalls?.length) return;
      for (const fc of functionCalls) {
        if (fc.name !== GEMINI_LIVE_SCRIPT_TOOL_NAME) continue;
        suppressTranscriptUpdateRef.current = false;
        const args = fc.args || {};
        const userTranscript =
          typeof args.userTranscript === 'string' ? args.userTranscript : '';
        // Synthetic client-initiated calls (verbal-stop fast path) originate from
        // inputTranscription that already passed the mic-open gate, so they are
        // inherently real user audio. For genuine
        // model-issued calls, require that real user audio arrived since the last
        // assistant turn — otherwise the model fabricated the transcript (it
        // predicted/spoke the patient's answer) and we must drop it.
        const isSyntheticClientCall =
          typeof fc.id === 'string' && fc.id.startsWith('offscript');
        const hasRealUserAudio = isSyntheticClientCall || realUserAudioDetectedRef.current;
        const safeUserTranscript = userTranscript && hasRealUserAudio ? userTranscript : '';
        let result = null;
        try {
          result = await Promise.resolve(
            onPauseDetectedRef.current?.({
              userTranscript: safeUserTranscript,
              conversationLog: conversationLogRef.current,
              questionsAsked: questionsAskedRef.current,
              context: contextRef.current,
            })
          );
        } catch (e) {
          console.error('[GeminiLive] onPauseDetected error', e);
          session.sendToolResponse({
            functionResponses: [
              {
                id: fc.id,
                name: fc.name,
                response: {
                  error: String(e?.message || e),
                  nextUtterance: "I'm sorry, something went wrong. Please try again.",
                  isComplete: false,
                },
              },
            ],
          });
          continue;
        }

        const question = result?.question ?? null;
        const isComplete = !!result?.isComplete;
        const stopImmediately = !!result?.stopImmediately;
        const preserveExactText = !!result?.preserveExactText;

        if (safeUserTranscript.trim()) {
          adapterRef.current.commitUserTurn(safeUserTranscript.trim());
          onUserSpeechRef.current?.(safeUserTranscript.trim());
        }

        if (stopImmediately) {
          haltLiveAssistantPlayback();
          intentionalDisconnectRef.current = true;
          conversationCompletingRef.current = true;
          if (!isSyntheticClientCall) {
            session.sendToolResponse({
              functionResponses: [
                {
                  id: fc.id,
                  name: fc.name,
                  response: {
                    nextUtterance: '',
                    isComplete: false,
                    endSessionNow: true,
                  },
                },
              ],
            });
          }
          scheduleVerbalEndConversationAck(setEndConversationAcknowledged, () => {
            stopConversationRef.current?.();
          });
          continue;
        }

        if (question) {
          // pendingScriptedQuestionRef removed — was dead scaffolding (set, never read)
          adapterRef.current.seedAssistantTurn(question);
          adapterRef.current.commitAssistantTurn(question);
          beginAssistantTurn(question);
          if (preserveExactText) {
            suppressTranscriptUpdateRef.current = true;
          }
          setQuestionsAsked((prev) => {
            const next = prev.includes(question) ? prev : [...prev, question];
            questionsAskedRef.current = next;
            return next;
          });
          if (isMedicolegalDisclaimerText(question)) {
            disclaimerDeliveredRef.current = true;
            clearPostOpeningDisclaimerTimer();
            const outCtx = outputAudioContextRef.current;
            if (outCtx) {
              micOpenAtRef.current = Math.max(
                micOpenAtRef.current,
                outCtx.currentTime + MEDICOLEGAL_DISCLAIMER_ESTIMATED_SPEECH_S
              );
            }
          }
        }

        console.log('[GeminiLive] sendToolResponse', {
          id: fc.id,
          nextUtterance: (question || '').substring(0, 60),
          isSynthetic: typeof fc.id === 'string' && fc.id.includes('-'),
        });
        session.sendToolResponse({
          functionResponses: [
            {
              id: fc.id,
              name: fc.name,
              response: {
                nextUtterance: question || '',
                isComplete,
              },
            },
          ],
        });

        const deferredTranscript = String(result?.deferredUserTranscript || '').trim();
        if (deferredTranscript && isActiveRef.current && sessionRef.current === session) {
          const deferMs = isMedicolegalDisclaimerText(question)
            ? MEDICOLEGAL_DISCLAIMER_ESTIMATED_SPEECH_S * 1000 + DEFERRED_BRAIN_TURN_AFTER_DISCLAIMER_MS
            : DEFERRED_BRAIN_TURN_AFTER_DISCLAIMER_MS;
          clearPostOpeningDisclaimerTimer();
          postOpeningDisclaimerTimerRef.current = setTimeout(() => {
            postOpeningDisclaimerTimerRef.current = null;
            if (!isActiveRef.current || sessionRef.current !== session) return;
            adapterRef.current.replayDeferredUserTurn(deferredTranscript);
            void Promise.resolve(
              onPauseDetectedRef.current?.({
                userTranscript: deferredTranscript,
                conversationLog: conversationLogRef.current,
                questionsAsked: questionsAskedRef.current,
                context: contextRef.current,
              })
            ).then((brainResult) => {
              if (!isActiveRef.current || sessionRef.current !== session) return;
              const deferredQuestion = brainResult?.question ?? null;
              if (deferredQuestion) {
                adapterRef.current.seedAssistantTurn(deferredQuestion);
                adapterRef.current.commitAssistantTurn(deferredQuestion);
                beginAssistantTurn(deferredQuestion);
                setQuestionsAsked((prev) => {
                  const next = prev.includes(deferredQuestion) ? prev : [...prev, deferredQuestion];
                  questionsAskedRef.current = next;
                  return next;
                });
                if (isMedicolegalDisclaimerText(deferredQuestion)) {
                  disclaimerDeliveredRef.current = true;
                  clearPostOpeningDisclaimerTimer();
                  const outCtx = outputAudioContextRef.current;
                  if (outCtx) {
                    micOpenAtRef.current = Math.max(
                      micOpenAtRef.current,
                      outCtx.currentTime + MEDICOLEGAL_DISCLAIMER_ESTIMATED_SPEECH_S
                    );
                  }
                }
                // Do NOT use sendRealtimeInput({ text }) here — that injects the
                // scripted question as USER input and the model answers its own
                // question (the hallucination regression). The deferredQuestion is
                // already committed to the log/UI above; nudge the model to take a
                // turn so it issues a fresh get_next_utterance and the normal
                // handleToolCalls path speaks the next scripted line. No user text
                // is injected.
                try {
                  session.sendClientContent({ turnComplete: true });
                } catch (e) {
                  console.warn('[GeminiLive] deferred turn nudge failed', e);
                }
              }
              if (brainResult?.stopImmediately) {
                haltLiveAssistantPlayback();
                intentionalDisconnectRef.current = true;
                conversationCompletingRef.current = true;
                scheduleVerbalEndConversationAck(setEndConversationAcknowledged, () => {
                  stopConversationRef.current?.();
                });
              }
            });
          }, deferMs);
        }

        if (isComplete) {
          conversationCompletingRef.current = true;
          const nowMs = Date.now();
          completionStopDeadlineRef.current = nowMs + MAX_COMPLETE_TEARDOWN_MS;
          setTimeout(() => {
            if (!conversationCompletingRef.current) return;
            scheduleCompletionStopAfterPlaybackDrain();
          }, MIN_COMPLETE_TEARDOWN_MS);
        }
      }
    },
    [
      beginAssistantTurn,
      clearPostOpeningDisclaimerTimer,
      haltLiveAssistantPlayback,
      scheduleCompletionStopAfterPlaybackDrain,
      setQuestionsAsked,
    ]
  );

  const stopConversationRef = useRef(null);

  /**
   * Stop mic → processor → WS sends before closing the socket (avoids sendRealtimeInput while CLOSING).
   * Ref assignment so onclose/onerror always see current implementation.
   */
  const releaseLiveResourcesRef = useRef(null);
  releaseLiveResourcesRef.current = ({ callOnComplete = false } = {}) => {
    if (captureNodeRef.current) {
      try {
        const node = captureNodeRef.current;
        if (node.port) node.port.onmessage = null;
        node.disconnect();
      } catch (_) {
        /* ignore */
      }
      captureNodeRef.current = null;
    }
    if (sessionRef.current) {
      try {
        sessionRef.current.close?.();
      } catch (_) {
        /* ignore */
      }
      sessionRef.current = null;
    }
    if (inputAudioContextRef.current) {
      inputAudioContextRef.current.close?.().catch(() => {});
      inputAudioContextRef.current = null;
    }
    if (outputAudioContextRef.current) {
      outputAudioContextRef.current.close?.().catch(() => {});
      outputAudioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    nextPlayTimeRef.current = 0;
    micOpenAtRef.current = 0;
    for (const src of activeOutputSourcesRef.current) {
      try {
        src.stop();
      } catch (_) {
        /* ignore */
      }
    }
    activeOutputSourcesRef.current = [];
    assistantTranscriptBufferRef.current = '';
    assistantTurnExpectedTextRef.current = '';
    if (assistantTranscriptFinalizeTimerRef.current) {
      clearTimeout(assistantTranscriptFinalizeTimerRef.current);
      assistantTranscriptFinalizeTimerRef.current = null;
    }
    clearCompletionDrainTimer();
    clearPostOpeningDisclaimerTimer();
    completionStopDeadlineRef.current = 0;
    if (callOnComplete) {
      onCompleteRef.current?.(conversationLogRef.current);
    }
    clearConnectionReadyPromise();
  };

  const stopConversation = useCallback(() => {
    intentionalDisconnectRef.current = true;
    conversationCompletingRef.current = false;
    completionStopDeadlineRef.current = 0;
    suppressTranscriptUpdateRef.current = false;
    clearCompletionDrainTimer();
    clearPostOpeningDisclaimerTimer();
    clearConnectionReadyPromise({ rejectReason: new Error('Live connection cancelled') });
    isActiveRef.current = false;
    setIsActive(false);
    setIsStarting(false);
    setIsListening(false);
    setIsSpeaking(false);
    setAudioConfirmed(false);
    releaseLiveResourcesRef.current?.({ callOnComplete: true });
  }, [
    clearCompletionDrainTimer,
    clearPostOpeningDisclaimerTimer,
    clearConnectionReadyPromise,
  ]);

  stopConversationRef.current = stopConversation;

  const playModelAudioParts = useCallback((parts) => {
    const ctx = outputAudioContextRef.current;
    if (!ctx || !parts?.length) return;
    for (const part of parts) {
      const inline = part?.inlineData;
      if (!inline?.data) continue;
      const mime = inline.mimeType || 'audio/pcm;rate=24000';
      const rate = parsePcmRateFromMime(mime, OUTPUT_PCM_RATE);
      try {
        const buffer = decodeLittleEndianPcm16Base64(inline.data, rate, ctx);
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        const startAt = Math.max(ctx.currentTime, nextPlayTimeRef.current);
        src.connect(ctx.destination);
        src.start(startAt);
        nextPlayTimeRef.current = startAt + buffer.duration;
        micOpenAtRef.current = nextPlayTimeRef.current + ASSISTANT_PLAYBACK_TAIL_S;
        activeOutputSourcesRef.current.push(src);
        setIsSpeaking(true);
        src.onended = () => {
          activeOutputSourcesRef.current = activeOutputSourcesRef.current.filter((s) => s !== src);
          if (ctx.currentTime >= nextPlayTimeRef.current - 0.01) {
            setIsSpeaking(false);
            if (conversationCompletingRef.current) {
              scheduleCompletionStopAfterPlaybackDrain();
            }
          }
        };
      } catch (e) {
        console.warn('[GeminiLive] PCM playback skipped', e);
      }
    }
  }, [scheduleCompletionStopAfterPlaybackDrain]);

  const resetSessionForStart = useCallback(() => {
    intentionalDisconnectRef.current = false;
    conversationCompletingRef.current = false;
    setEndConversationAcknowledged(false);
    setError(null);
    setIsStarting(true);
    isActiveRef.current = true;
    setIsActive(true);
    setQuestionsAsked([]);
    questionsAskedRef.current = [];
    setConversationLog([]);
    conversationLogRef.current = [];
    adapterRef.current?.reset();
    disclaimerDeliveredRef.current = false;
    suppressTranscriptUpdateRef.current = false;
    beginAssistantTurn('');
  }, [beginAssistantTurn, setConversationLog, setQuestionsAsked]);

  const prepareLiveConnection = useCallback(async () => {
    if (isActiveRef.current) return;

    resetSessionForStart();

    const connectionReadyPromise = new Promise((resolve, reject) => {
      connectionReadyHandlersRef.current = { resolve, reject };
    });

    try {
      const { ephemeralToken, model } = await fetchGeminiLiveEphemeralToken();
      liveModelIdRef.current = model;

      const outputCtx = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: OUTPUT_PCM_RATE,
      });
      outputAudioContextRef.current = outputCtx;
      nextPlayTimeRef.current = outputCtx.currentTime;
      micOpenAtRef.current = 0;
      activeOutputSourcesRef.current = [];

      await outputCtx.resume().catch(() => {});

      const ai = new GoogleGenAI({
        apiKey: ephemeralToken,
        httpOptions: { apiVersion: 'v1alpha' },
      });

      const systemInstruction = buildSystemInstruction({
        openingMessage: openingMessageRef.current,
        language: languageRef.current,
        askFirstQuestionAfterOpening: askFirstQuestionAfterOpeningRef.current,
        askDisclaimerAfterOpening: askDisclaimerAfterOpeningRef.current,
        disclaimerText: askDisclaimerAfterOpeningRef.current
          ? getMedicolegalDisclaimer(contextRef.current?.prompts)
          : '',
        voicePrompts: contextRef.current?.prompts,
      });

      const origWarn = console.warn;
      console.warn = (...args) => {
        const first = args[0];
        if (
          typeof first === 'string' &&
          first.includes('Ephemeral token support is experimental')
        ) {
          return;
        }
        origWarn.apply(console, args);
      };
      let session;
      try {
        const voiceName = liveSpeechVoiceRef.current || GEMINI_LIVE_DEFAULT_VOICE;
        session = await ai.live.connect({
          model,
          config: {
            responseModalities: [Modality.AUDIO],
            systemInstruction,
            // Deterministic decoding: temperature 0 removes the creative sampling
            // that let the model predict and speak the patient's answers during
            // post-question silence. The scripted brain (get_next_utterance) is the
            // only source of substantive content, so no sampling diversity is needed.
            temperature: 0,
            speechConfig: {
              voiceConfig: {
                prebuiltVoiceConfig: { voiceName },
              },
            },
            inputAudioTranscription: {},
            outputAudioTranscription: {},
            tools: [{ functionDeclarations: [buildScriptToolDeclaration()] }],
            toolConfig: {
              functionCallingConfig: {
                mode: FunctionCallingConfigMode.ANY,
                allowedFunctionNames: [GEMINI_LIVE_SCRIPT_TOOL_NAME],
              },
            },
          },
          callbacks: {
            onopen: () => {
              setIsStarting(false);
              setIsListening(true);
              resolveConnectionReady();
            },
            onmessage: async (msg) => {
              const _sc = msg.serverContent;
              const _audioParts = (_sc?.modelTurn?.parts || []).filter(
                (p) => p?.inlineData?.data
              ).length;
              console.log('[GeminiLive][trace]', {
                toolCall: msg.toolCall?.functionCalls?.map((f) => f.name) || null,
                modelAudioParts: _audioParts,
                interrupted: !!_sc?.interrupted,
                inputTx: _sc?.inputTranscription?.text ? _sc.inputTranscription.text.slice(0, 40) : null,
                outputTx: _sc?.outputTranscription?.text ? _sc.outputTranscription.text.slice(0, 40) : null,
                turnComplete: !!_sc?.turnComplete,
                generationComplete: !!_sc?.generationComplete,
              });
              if (msg.toolCall?.functionCalls?.length) {
                await handleToolCalls(session, msg.toolCall.functionCalls);
              }
              const sc = msg.serverContent;
              if (sc?.modelTurn?.parts) {
                playModelAudioParts(sc.modelTurn.parts);
              }
              if (sc?.inputTranscription?.text) {
                const txText = sc.inputTranscription.text;
                // Always surface for live captions, even when gated below.
                onLiveTranscriptRef.current?.(txText);

                if (conversationCompletingRef.current || intentionalDisconnectRef.current) {
                  return;
                }

                // Only treat this as user speech when the mic gate is open. While
                // the assistant is speaking (or during the post-playback tail),
                // inputTranscription is echo or leaked model audio, not the user.
                const inputOutCtx = outputAudioContextRef.current;
                const micGateOpen =
                  !inputOutCtx || inputOutCtx.currentTime >= micOpenAtRef.current;
                if (!micGateOpen) {
                  return;
                }
                realUserAudioDetectedRef.current = true;

                if (shouldEndConversationFromUserSpeech(txText)) {
                  void handleToolCalls(sessionRef.current, [
                    {
                      name: GEMINI_LIVE_SCRIPT_TOOL_NAME,
                      args: { userTranscript: txText },
                      id: `offscript-stop-${Date.now()}`,
                    },
                  ]);
                }
              }
              if (sc?.outputTranscription?.text) {
                absorbAssistantTranscriptionChunk(sc.outputTranscription.text);
              }
              if (sc?.interrupted) {
                // INVARIANT: Only treat a server `interrupted` as a real assistant
                // cut-off when audio was actually playing. When activeSources is
                // empty the question already finished and this event is the patient
                // starting to answer (normal Gemini VAD barge-in) — tagging
                // "(cut off)" then would mislabel a completed question. A verbal
                // "stop" spoken mid-question still has activeSources > 0, so it is
                // still tagged correctly.
                const hadActiveAudio = activeOutputSourcesRef.current.length > 0;
                const octx = outputAudioContextRef.current;
                const nowT = octx?.currentTime || 0;
                for (const src of activeOutputSourcesRef.current) {
                  try {
                    src.stop();
                  } catch (_) {
                    /* ignore */
                  }
                }
                activeOutputSourcesRef.current = [];
                nextPlayTimeRef.current = nowT;
                micOpenAtRef.current = nowT;
                if (hadActiveAudio) {
                  markLastAssistantInterrupted();
                }
                setIsSpeaking(false);
              }
            },
            onerror: (e) => {
              console.error('[GeminiLive] socket error', e);
              setError(e?.message || 'Live connection error');
              clearConnectionReadyPromise({
                rejectReason: e?.message || 'Live connection error',
              });
              if (isActiveRef.current) {
                isActiveRef.current = false;
                setIsActive(false);
                setIsStarting(false);
                setIsListening(false);
                setIsSpeaking(false);
                setAudioConfirmed(false);
                releaseLiveResourcesRef.current?.({ callOnComplete: false });
              }
            },
            onclose: (ev) => {
              if (sessionRef.current !== session) return;
              if (!isActiveRef.current) return;
              const expectClose =
                conversationCompletingRef.current || intentionalDisconnectRef.current;
              conversationCompletingRef.current = false;
              intentionalDisconnectRef.current = false;
              if (!expectClose) {
                clearConnectionReadyPromise({
                  rejectReason: 'Live connection ended',
                });
              } else {
                clearConnectionReadyPromise();
              }
              isActiveRef.current = false;
              setIsActive(false);
              setIsStarting(false);
              setIsListening(false);
              setIsSpeaking(false);
              setAudioConfirmed(false);
              releaseLiveResourcesRef.current?.({ callOnComplete: false });
              if (!expectClose) {
                const reason = ev && typeof ev.reason === 'string' && ev.reason.trim() ? ev.reason.trim() : '';
                const code = ev && typeof ev.code === 'number' ? ev.code : null;
                console.warn('[GeminiLive] WebSocket closed', code, reason || '(no reason)');
                setError((prev) => {
                  if (prev) return prev;
                  if (reason) return `Live connection ended: ${reason}`;
                  if (code != null && code !== 1000) return `Live connection ended (WebSocket code ${code}).`;
                  return 'Live connection ended.';
                });
              }
            },
          },
        });
        sessionRef.current = session;
      } finally {
        console.warn = origWarn;
      }

      await connectionReadyPromise;
    } catch (e) {
      console.error('[GeminiLive] prepareLiveConnection failed', e);
      clearConnectionReadyPromise({
        rejectReason: e?.message || 'Could not connect live voice',
      });
      setError(e?.message || 'Could not connect live voice');
      stopConversation();
      throw e;
    }
  }, [
    resetSessionForStart,
    resolveConnectionReady,
    clearConnectionReadyPromise,
    absorbAssistantTranscriptionChunk,
    handleToolCalls,
    markLastAssistantInterrupted,
    playModelAudioParts,
    stopConversation,
  ]);

  const beginLiveAudio = useCallback(async () => {
    if (!connectionReadyRef.current) {
      throw new Error('beginLiveAudio() called before prepareLiveConnection() completed');
    }

    const session = sessionRef.current;
    if (!session) {
      throw new Error('beginLiveAudio() called without an active Live session');
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
      });
      mediaStreamRef.current = stream;
      setAudioConfirmed(true);

      const inputCtx = new (window.AudioContext || window.webkitAudioContext)();
      inputAudioContextRef.current = inputCtx;
      await inputCtx.resume().catch(() => {});

      const openingText = openingMessageRef.current;
      const disclaimerText = askDisclaimerAfterOpeningRef.current
        ? getMedicolegalDisclaimer(contextRef.current?.prompts)
        : '';
      if (askDisclaimerAfterOpeningRef.current && disclaimerText) {
        adapterRef.current.commitAssistantTurn(disclaimerText);
      }
      adapterRef.current.seedAssistantTurn(openingText);
      adapterRef.current.commitAssistantTurn(openingText);
      beginAssistantTurn(openingText);
      const model = liveModelIdRef.current;
      try {
        if (prefersRealtimeTextOpeningNudge(model)) {
          session.sendRealtimeInput({ text: ' ' });
        } else {
          session.sendClientContent({ turnComplete: true });
        }
      } catch (sendErr) {
        console.warn('[GeminiLive] opening nudge failed', sendErr);
      }
      if (askDisclaimerAfterOpeningRef.current && !disclaimerDeliveredRef.current) {
        disclaimerDeliveredRef.current = true;
        onDisclaimerDeliveredRef.current?.();
      }

      const source = inputCtx.createMediaStreamSource(stream);
      const muteGain = inputCtx.createGain();
      muteGain.gain.value = 0;

      const pumpCaptureFrame = (inputData) => {
        const sess = sessionRef.current;
        if (!sess || !isActiveRef.current || !isLiveSessionSendReady(sess)) return;
        const outCtx = outputAudioContextRef.current;
        if (outCtx && outCtx.currentTime < micOpenAtRef.current) return;
        const down = downsampleBuffer(inputData, inputCtx.sampleRate, INPUT_PCM_RATE);
        const pcm = floatTo16BitPCM(down);
        const audio = {
          mimeType: `audio/pcm;rate=${INPUT_PCM_RATE}`,
          data: encodeLittleEndianPcm16Base64(pcm),
        };
        try {
          if (!isLiveSessionSendReady(sess)) return;
          sess.sendRealtimeInput({ audio });
        } catch (err) {
          const msg = err?.message || String(err);
          if (msg.includes('CLOSING') || msg.includes('CLOSED')) {
            releaseLiveResourcesRef.current?.({ callOnComplete: false });
            isActiveRef.current = false;
            setIsActive(false);
            setIsListening(false);
            return;
          }
          console.warn('[GeminiLive] sendRealtimeInput', err);
        }
      };

      if (inputCtx.audioWorklet) {
        const workletUrl = URL.createObjectURL(
          new Blob([GEMINI_LIVE_CAPTURE_WORKLET], { type: 'application/javascript' })
        );
        try {
          await inputCtx.audioWorklet.addModule(workletUrl);
        } finally {
          URL.revokeObjectURL(workletUrl);
        }
        const captureNode = new AudioWorkletNode(inputCtx, 'gemini-live-capture', {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          channelCount: 1,
        });
        captureNodeRef.current = captureNode;
        captureNode.port.onmessage = (ev) => {
          if (ev.data instanceof Float32Array) pumpCaptureFrame(ev.data);
        };
        source.connect(captureNode);
        captureNode.connect(muteGain);
      } else {
        const bufferSize = 4096;
        const processor = inputCtx.createScriptProcessor(bufferSize, 1, 1);
        captureNodeRef.current = processor;
        processor.onaudioprocess = (ev) => {
          pumpCaptureFrame(ev.inputBuffer.getChannelData(0));
        };
        source.connect(processor);
        processor.connect(muteGain);
      }
      muteGain.connect(inputCtx.destination);

      setIsListening(true);
    } catch (e) {
      console.error('[GeminiLive] beginLiveAudio failed', e);
      setError(e?.message || 'Could not start live voice');
      stopConversation();
      throw e;
    }
  }, [beginAssistantTurn, stopConversation]);

  const startConversation = useCallback(async () => {
    await prepareLiveConnection();
    await beginLiveAudio();
  }, [prepareLiveConnection, beginLiveAudio]);

  const mediaSupported =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

  return {
    isActive,
    isStarting,
    isListening,
    isSpeaking,
    currentQuestion: '',
    questionsAsked,
    conversationLog,
    audioConfirmed,
    isAcceptingInput: !isSpeaking && !conversationCompletingRef.current,
    speechSupported: mediaSupported,
    error,
    endConversationAcknowledged,
    startConversation,
    prepareLiveConnection,
    beginLiveAudio,
    stopConversation,
    conversationScrollRef,
    supportedLanguages: [],
  };
}

export default useGeminiLiveVoiceConversationEngine;
export { buildSystemInstruction };
